// src/components/accesEnseignant/apiAccesEnseignant.js
// Appels de l'administration vers /api/acces-enseignant (jeton employé).
//
// C'est le jeton de l'ADMINISTRATION qui part ici (getAuthHeaders, clé
// `token`) : ces routes sont celles de l'école qui remet un code à un
// enseignant, pas celles de l'enseignant connecté à son espace. Le jeton de
// l'espace enseignant vit ailleurs (src/espace-enseignant/api.js) et n'est
// jamais lu par ce fichier.

import { getAuthHeaders } from '../../services/api';

const RACINE = '/api/acces-enseignant';

const LICENCE_FERMEE = "L'espace enseignant n'est pas ouvert pour cet établissement.";

// Le refus de licence (`requireFeature`) ne porte pas de `message` mais un
// `error` au libellé technique (« Le module … n'est pas inclus dans votre
// licence ») : on le reconnaît à son code pour dire la même chose que le reste
// de l'écran. Les autres refus du serveur ont leur `message` en français, qu'on
// affiche tel quel.
function erreurDuServeur(reponse, donnees, defaut) {
  let message = donnees?.message;
  if (!message && (donnees?.code === 'FEATURE_NOT_LICENSED' || reponse.status === 403)) message = LICENCE_FERMEE;
  const erreur = new Error(message || donnees?.error || defaut);
  erreur.code = donnees?.code;
  erreur.statut = reponse.status;
  return erreur;
}

async function appeler(chemin, { methode = 'GET', corps } = {}) {
  let reponse;
  try {
    reponse = await fetch(`${RACINE}${chemin}`, {
      method: methode,
      headers: getAuthHeaders(),
      body: corps !== undefined ? JSON.stringify(corps) : undefined
    });
  } catch {
    throw new Error('Connexion au serveur impossible.');
  }
  let donnees = null;
  try { donnees = await reponse.json(); } catch { donnees = null; }
  if (!reponse.ok) throw erreurDuServeur(reponse, donnees, 'Une erreur est survenue.');
  return donnees;
}

// La fiche se demande en POST avec le code dans le corps, jamais dans
// l'adresse : une adresse se retrouve dans les journaux du serveur et dans
// l'historique du navigateur, le code en clair n'a rien à y faire. Pour la
// même raison, le NOM DU FICHIER ne le contient pas : il resterait dans la
// liste des téléchargements du poste de l'école bien après que la fiche a été
// remise et le PDF supprimé.
async function telechargerPdf(chemin, corps, nom) {
  let reponse;
  try {
    reponse = await fetch(`${RACINE}${chemin}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(corps)
    });
  } catch {
    throw new Error('Connexion au serveur impossible.');
  }
  if (!reponse.ok) {
    let donnees = null;
    try { donnees = await reponse.json(); } catch { donnees = null; }
    throw erreurDuServeur(reponse, donnees, 'Impossible de préparer la fiche.');
  }
  const blob = await reponse.blob();
  const url = URL.createObjectURL(blob);
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nom;
  document.body.appendChild(lien);
  lien.click();
  lien.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

/**
 * Nom du PDF : celui de l'enseignant, sans accent ni espace, pour que le
 * secrétariat retrouve sa fiche dans ses téléchargements. Jamais le code.
 */
export const nomFicheAcces = (employe) => {
  const nom = [employe?.prenom, employe?.nom]
    .filter(Boolean)
    .join(' ')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
  return nom ? `acces-espace-enseignant-${nom}.pdf` : 'acces-espace-enseignant.pdf';
};

export const accesEnseignant = {
  employe: (employeId) => appeler(`/employes/${employeId}`),
  creerCode: (employeId) => appeler(`/employes/${employeId}/codes`, { methode: 'POST' }),
  annulerCode: (codeId) => appeler(`/codes/${codeId}/annuler`, { methode: 'POST' }),
  fiche: (code, nomFichier) => telechargerPdf('/codes/fiche', { code }, nomFichier || 'acces-espace-enseignant.pdf'),
  reinitialiser: (compteId) => appeler(`/comptes/${compteId}/reinitialisation`, { methode: 'POST' }),
  statutCompte: (compteId, statut, motif) => appeler(`/comptes/${compteId}/statut`, { methode: 'POST', corps: { statut, motif } })
};
